// packages/studio/web/src/repos/browser/FileTree.tsx
//
// Left-rail tree listing files at a given ref.
// Calls GET /api/ws/:slug/r/:repo/tree/:ref (root, flat listing)
// and renders blob entries as links to the browser route.

import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "@archlang/ui";
import { useContextMenu } from "../../ui/ContextMenu";
import { Spinner } from "../../ui/Spinner";
import { cx } from "../../lib/classNames";
import { repoFiles, repoHome } from "../../lib/paths";
import { useTranslation } from "../../i18n";

export interface FileTreeProps {
  readonly wsSlug: string;
  readonly repoSlug: string;
  readonly ref: string;
  readonly selectedPath: string;
  /** Which route file links target. "files" = read-only browser (default),
   *  "edit" = the editor (used when the tree is the editor's own rail). */
  readonly linkTo?: "files" | "edit";
}

interface TreeEntry {
  readonly name: string;
  readonly kind: "blob" | "tree";
  readonly oid: string;
}

interface TreePayload {
  readonly path: string;
  readonly entries: TreeEntry[];
}

interface PackagesPayload {
  readonly packages: { path: string; name?: string; implicit?: boolean }[];
  readonly spaces: { path: string; name?: string }[];
}

class TreeFetchError extends Error {
  constructor(public status: number) {
    super(`tree fetch failed: ${status}`);
  }
}

export function FileTree({ wsSlug, repoSlug, ref, selectedPath }: FileTreeProps) {
  const { t } = useTranslation("repos");
  const navigate = useNavigate();
  const menu = useContextMenu();
  const q = useQuery({
    queryKey: ["tree", wsSlug, repoSlug, ref],
    queryFn: async ({ signal }): Promise<TreePayload> => {
      const r = await fetch(
        `/api/ws/${encodeURIComponent(wsSlug)}/r/${encodeURIComponent(repoSlug)}/tree/${encodeURIComponent(ref)}`,
        { credentials: "include", signal },
      );
      if (!r.ok) throw new TreeFetchError(r.status);
      return r.json();
    },
    staleTime: 30 * 1000,
    retry: false,
  });

  // Monorepo package discovery for this ref. Surfaces packages that live in
  // subdirectories (the flat root listing below can't show them). Best-effort:
  // a failure just hides the section.
  const pkgQuery = useQuery({
    queryKey: ["packages", wsSlug, repoSlug, ref],
    queryFn: async ({ signal }): Promise<PackagesPayload> => {
      const r = await fetch(
        `/api/ws/${encodeURIComponent(wsSlug)}/r/${encodeURIComponent(repoSlug)}/packages/${encodeURIComponent(ref)}`,
        { credentials: "include", signal },
      );
      if (!r.ok) throw new TreeFetchError(r.status);
      return r.json();
    },
    staleTime: 30 * 1000,
    retry: false,
  });
  // Only declared, named packages in subdirectories are worth listing — the
  // implicit repo-root package is just "the files below".
  const declaredPackages = (pkgQuery.data?.packages ?? []).filter(
    (p) => p.path !== "" && !p.implicit,
  );

  const entries = (q.data?.entries ?? [])
    .filter((e) => e.kind === "blob")
    .sort((a, b) => a.name.localeCompare(b.name));

  if (q.isPending) {
    return (
      <div className="flex items-center gap-2 p-3 text-sm text-slate-500" aria-busy="true">
        <Spinner size="sm" />
        {t("tree.loading")}
      </div>
    );
  }

  if (q.isError) {
    const is404 = q.error instanceof TreeFetchError && q.error.status === 404;
    if (is404) {
      return (
        <div className="p-3" role="status">
          <p className="mb-1 text-sm font-medium text-slate-300">{t("tree.emptyTitle")}</p>
          <p className="mb-3 text-xs text-slate-500">
            {t("tree.emptyNoCommits", { ref })}
          </p>
          <Link
            to={repoHome(wsSlug, repoSlug)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-arch-600 px-3 text-xs font-medium text-white transition-colors hover:bg-arch-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arch-400"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {t("tree.createFirstFile")}
          </Link>
          <details className="mt-3 text-xs text-slate-500">
            <summary className="cursor-pointer select-none hover:text-slate-400">{t("tree.orPushFromClone")}</summary>
            <pre className="mt-2 overflow-auto rounded-lg border border-slate-800 bg-slate-950 p-2.5 font-mono text-[0.7rem] leading-relaxed text-slate-400">
{`git init my-repo && cd my-repo
echo "module Foo {}" > main.arch
git add . && git commit -m "init"
git remote add origin ${window.location.origin}${repoHome(wsSlug, repoSlug)}
git push -u origin main
# (sign in with your email + password, or a PAT: settings → tokens)`}
            </pre>
          </details>
        </div>
      );
    }
    return (
      <div className="p-3 text-sm text-red-400" role="alert">
        {t("tree.loadFailed")}
      </div>
    );
  }

  return (
    <>
      {declaredPackages.length > 0 ? (
        <div className="border-b border-slate-800 px-2 pb-2 pt-1">
          <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
            {t("tree.packages")}
          </p>
          <ul>
            {declaredPackages.map((p) => (
              <li key={p.path}>
                <Tooltip content={t("tree.packageRootTooltip", { path: p.path })}>
                  <Link
                    to={repoHome(wsSlug, repoSlug)}
                    onContextMenu={(e) =>
                      menu.open(e, [
                        {
                          label: t("menu.open"),
                          onClick: () => void navigate(repoHome(wsSlug, repoSlug)),
                        },
                        {
                          label: t("menu.copyPath"),
                          onClick: () => void navigator.clipboard.writeText(p.path),
                        },
                      ])
                    }
                    className="flex items-center gap-2 rounded-md px-2 py-1 text-[13px] text-slate-300 transition-colors hover:bg-slate-800/50 hover:text-slate-100"
                  >
                    <span className="shrink-0 rounded bg-arch-600/30 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-arch-300">
                      pkg
                    </span>
                    <span className="truncate font-medium">{p.name ?? p.path}</span>
                    <span className="truncate text-[11px] text-slate-600">{p.path}</span>
                  </Link>
                </Tooltip>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <ul className="p-2" role="tree" aria-label={t("tree.filesAriaLabel")}>
        {entries.map((e) => {
        const active = e.name === selectedPath;
        return (
          <li
            key={e.name}
            role="treeitem"
            aria-current={active ? "page" : undefined}
            aria-selected={active}
          >
            <Link
              to={repoFiles(wsSlug, repoSlug, ref, e.name)}
              onContextMenu={(ev) =>
                menu.open(ev, [
                  {
                    label: t("menu.open"),
                    onClick: () => void navigate(repoFiles(wsSlug, repoSlug, ref, e.name)),
                  },
                  {
                    label: t("menu.copyPath"),
                    onClick: () => void navigator.clipboard.writeText(e.name),
                  },
                ])
              }
              className={cx(
                "flex items-center gap-2 rounded-md px-2 py-1.5 font-mono text-[13px] transition-colors",
                active
                  ? "bg-slate-800 text-slate-100"
                  : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200",
              )}
            >
              <svg className={cx("h-3.5 w-3.5 shrink-0", active ? "text-arch-400" : "text-slate-600")} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
                <path d="M13 2v7h7" />
              </svg>
              <span className="truncate">{e.name}</span>
            </Link>
          </li>
        );
        })}
      </ul>
      {menu.node}
    </>
  );
}
